import type { EmbeddedElementValue } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { isEmpty } from 'lodash'

import { t } from 'services/Translation'
import { getFileByUsage } from 'utils/files'

import * as s from './EmbeddedElement.module.scss'

const scormEventListenerLink = (id: string) => `
  <script>
    const handleMessage = (event) => {
      if (event?.data?.type === 'complete') {
         window.parent.postMessage({ type: 'complete', id: '${id}' }, '*')
      }
    }
    window.addEventListener('message', handleMessage)
  </script>
`

export const scormBridgeScript = (id: string) => `
        <script>
        class ScormBridge {
          constructor(id) {
              this.id = id
              this.initialized = false;
              this.errors = [];
              this.store = {};
              this.saveStoreTimer = null;
          }

          init() {
              this.initialized = true;
              this.errors = [];
              this.store = {};
              window.API = this;
              window.API_1484_11 = this;
              this.debug('SCORM Bridge initialized');
              this.debug('SCORM Bridge initialized, window.API =', this);
          }

          debug(...args) {
              console.log(...args);
          }

          onStoreChanged() {
              if (this.saveStoreTimer) {
                  clearTimeout(this.saveStoreTimer);
              }

              this.saveStoreTimer = setTimeout(() => {
                  this.sendStore();
                  this.saveStoreTimer = null;
              }, 500);
          }

          sendStore() { }

          interceptSetValue(key, value) {
              const isQuizResult = Boolean(window.defaultData?.settings?.sectionId)
              if (isQuizResult && /(?:cmi.success_status|cmi.core.lesson_status)/.test(key)) {
                  switch (value) {
                      case 'passed':
                        console.info('SCORM passed, sending message to parent');
                          window.parent.postMessage({ type: 'complete', id: this.id }, '*')
                          break;
                      case 'failed':
                          break;
                  }
              }
              if(!isQuizResult && /(?:cmi.completion_status|cmi.core.lesson_status)/.test(key)) {
                  switch (value) {
                      case 'completed':
                        console.info('SCORM completed, sending message to parent');
                          window.parent.postMessage({ type: 'complete', id: this.id }, '*')
                          break;
                      case 'failed':
                          break;
                  }
              }
          }

          _checkInitialized(func) {
              return (...args) => {
                  return this.initialized ? func.call(this, ...args) : 'false';
              };
          }

          _withErrorsCaught(func) {
              return (...args) => {
                  try {
                      return func.call(this, ...args);
                  } catch (error) {
                      this.errors.push({
                          code: -1,
                          message: error.message
                      });
                      return 'false';
                  }
              };
          }

          LMSInitialize() {
              return this._checkInitialized(this._withErrorsCaught(() => 'true'))();
          }

          LMSGetValue(key) {
              return this._checkInitialized(this._withErrorsCaught((k) => {
                  return this.store.hasOwnProperty(k) ? this.store[k] : '';
              }))(key);
          }

          LMSSetValue(key, value) {
              return this._checkInitialized(this._withErrorsCaught((k, v) => {
                  this.store[k] = v;
                  this.interceptSetValue(k, v);
                  this.onStoreChanged();
                  return 'true';
              }))(key, value);
          }

          LMSCommit() {
              return this._checkInitialized(() => {
                  this.sendStore();
                  return 'true';
              })();
          }

          LMSFinish() {
              return this._checkInitialized(this._withErrorsCaught(() => {
                  this.initialized = false;
                  return 'true';
              }))();
          }

          LMSGetLastError() {
              return this._checkInitialized(() => {
                  return this.errors.length > 0 ? this.errors[this.errors.length - 1].code : 0;
              })();
          }

          LMSGetErrorString() {
              return this._checkInitialized(() => {
                  return this.errors.length > 0 ? this.errors[this.errors.length - 1].message : '';
              })();
          }

          LMSGetDiagnostic() {
              return this.LMSGetErrorString();
          }

          Initialize() {
              return this.LMSInitialize();
          }

          GetValue(key) {
              return this.LMSGetValue(key);
          }

          SetValue(key, value) {
              return this.LMSSetValue(key, value);
          }

          Commit() {
              return this.LMSCommit();
          }

          Terminate() {
              return this.LMSFinish();
          }

          GetLastError() {
              return this.LMSGetLastError();
          }

          GetErrorString() {
              return this.LMSGetErrorString();
          }

          GetDiagnostic() {
              return this.LMSGetDiagnostic();
          }
      }

      const scormBridge = new ScormBridge('${id}');
      scormBridge.init();

    </script>`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const prepareHtml = (html?: string, iframeId?: string, scormBridgeScript?: string) => {
  /* If we decide to use this function in the future, we can add a script to send the height of the iframe back to the parent window.
    This script will calculate the height of the body and document, and send it as a message
    to the parent window. The iframeId can be used to identify which iframe is sending the message.
    This is useful for iframes that load content dynamically and need to adjust their height accordingly.
    This case is for iframes that are not same-origin and we can't access their content directly through contentWindow.

    Protocol: 'vedalib-iframe-height' - see services/IframeMessaging/schemas.ts

  const heightScript = `
    <script>
      (function() {
        let lastHeight = 0;
        let debounceTimer = null;

        function sendHeight() {
          const heights = [
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          ].filter(h => h > 0);

          if (heights.length === 0) return;

          const height = Math.max(...heights);
          if (height === lastHeight) return;

          lastHeight = height;

          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(function() {
            window.parent.postMessage({
              type: 'vedalib-iframe-height',
              height: height,
              ${iframeId ? `iframeId: '${iframeId}',` : ''}
            }, '*');
          }, 100);
        }

        // Send height on load
        window.addEventListener('load', sendHeight);

        // Send height on resize
        window.addEventListener('resize', sendHeight);

        // Monitor DOM changes with ResizeObserver
        if (window.ResizeObserver) {
          new ResizeObserver(sendHeight).observe(document.body);
        }

        // Monitor DOM mutations
        new MutationObserver(sendHeight).observe(document.body, {
          childList: true, subtree: true, attributes: true
        });

        // Send initial height
        sendHeight();
      })();
    </script> `
*/

  return `
   ${scormBridgeScript ? scormBridgeScript : ''}
   <style>
      body, html { margin: 0; padding: 0; height: auto; }
   </style>
   <div id="wrapper">
    ${html}
   </div>`
}

export const getNumberFromString = (
  value: string | number | undefined,
  defaultValue: number = 0,
): number => {
  if (typeof value === 'number') {
    return value
  }

  if (!value) {
    return defaultValue
  }

  const parsed = parseFloat(value)
  return isNaN(parsed) ? defaultValue : parsed
}

export const isEmptyValue = (value: EmbeddedElementValue) => {
  if (!value) {
    return true
  }
  if (value.type === 'html') {
    return isEmpty(value.html)
  }
  if (value.type === 'link') {
    return isEmpty(value.link)
  }
  if (value.type === 'zip') {
    return !value.zipFile || !value.zipFile.id
  }
  return true
}

export const getInnerIframeData = (
  value: EmbeddedElementValue,
  iframeId?: string,
  createBlobUrl?: (html: string) => string,
) => {
  const result: {
    props: { src?: string }
    editorMessage?: React.ReactNode
    onlyScorm: boolean
  } = { onlyScorm: false, props: {} }
  if (value.type === 'html') {
    const htmlString = prepareHtml(value.html, iframeId)
    result.props.src = createBlobUrl
      ? createBlobUrl(htmlString)
      : URL.createObjectURL(new Blob([htmlString], { type: 'text/html' }))
  } else if (value.type === 'link') {
    if (value.link) {
      if (!value.link.startsWith('http') && !value.link.startsWith('//')) {
        result.onlyScorm = true
        result.editorMessage = <span>!!!RELATIVE PATH: not supported!!!</span>
      } else {
        result.props.src = value.link
      }
    }
  } else if (value.type === 'zip') {
    if (value.zipFile?.id) {
      const fileData = getFileByUsage(value.zipFile, FileUsageTypeEnum.embeddedArchive)
      const folder = fileData?.path?.split('/').pop()
      const cleanPathname = window.location.pathname
        .replace(/index.html$/, '')
        .replace(/\/$/, '')
      const isFileProtocol = window.location.protocol === 'file:'
      const baseUrl = isFileProtocol ? cleanPathname : window.location.origin + cleanPathname
      const path = `${baseUrl}/templates/content/${folder}/${value.zipPath || 'index.html'}`
      result.props.src = stripScormProxyParams(path)
      result.editorMessage = (
        <div className={s.help}>
          <p>{t('elements.embedded.help')}</p>
          <p>Embedded work only on SCORM.</p>
        </div>
      )
    }
    result.onlyScorm = true
  }
  return result
}

/**
 * Strip SCORM proxy parameters from URL to prevent nested courses
 * from interfering with parent course's SCORM communication.
 */
const stripScormProxyParams = (url: string): string => {
  try {
    const isFileProtocol = window.location.protocol === 'file:'
    if (isFileProtocol) {
      // For file:// protocol, URL constructor doesn't work with "null" origin
      // Just strip params manually if present
      const urlWithoutParams = url.split('?')[0]
      return urlWithoutParams
    }
    const urlObj = new URL(url, window.location.origin)
    urlObj.searchParams.delete('scorm_proxy')
    urlObj.searchParams.delete('scorm_channel')
    return urlObj.toString()
  } catch {
    return url
  }
}

export const makeIframeIsolation = (url: string) => {
  const htmlString = `
<html>
  <head>
    <script>
      Object.defineProperty(window, 'parent', {
        get: function() {
            return window;
        }
      });
    </script>
    <style>
      body, html { margin: 0; padding: 0; height: 100%; }
      iframe { width: 100%; height: 100%; border: none; }
    </style>
  </head>
  <body>
    <iframe src="${url}" sandbox='allow-scripts allow-same-origin allow-forms' />
  </body>
</html>
`
  const blob = new Blob([htmlString], { type: 'text/html' })
  return URL.createObjectURL(blob)
}

export const makeCustomScormApi = (url: string, id: string) => {
  const htmlString = `
    <html>
      <head>
         ${scormEventListenerLink(id)}
         ${scormBridgeScript(id)}
    <style>
      body, html { margin: 0; padding: 0; height: 100%; }
      iframe { width: 100%; height: 100%; border: none; }
    </style>
  </head>
  <body>
  <iframe src="${url}" sandbox="allow-scripts allow-same-origin allow-forms" />
  </body>
</html>
`
  const blob = new Blob([htmlString], { type: 'text/html' })
  return URL.createObjectURL(blob)
}
